if (!customElements.get('main-wishlist')) {
  customElements.define(
    'main-wishlist',
    class MainWishlist extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('[data-wishlist-grid]');
        this.template = this.querySelector('[data-wishlist-card]');
        this.status = this.querySelector('[data-wishlist-status]');
        this.empty = this.querySelector('[data-wishlist-empty]');
        this.banner = this.querySelector('[data-wishlist-banner]');
        this.bar = this.querySelector('[data-wishlist-bar]');
        this.checkoutButton = this.querySelector('[data-wishlist-checkout]');
        this.inStockVariants = [];

        this.checkoutButton.addEventListener('click', () => this.checkout());
        this.checkoutButton.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          this.checkout();
        });
        document.addEventListener('wishlist:change', () => this.render());
        // Back from checkout, the browser restores this page from its cache mid-"loading": reset.
        window.addEventListener('pageshow', (event) => {
          if (!event.persisted) return;
          this.checkingOut = false;
          this.checkoutButton.classList.remove('disabled');
          this.render();
        });

        // Mobile header shows the cart count on this page, as it did with Swym's layout.
        document.querySelector('.header_cart')?.classList.add('show-bubble');

        // assets/wishlist.js is a deferred script too; every deferred script has run by DOMContentLoaded.
        if (window.BluorngWishlist) this.render();
        else document.addEventListener('DOMContentLoaded', () => this.render(), { once: true });
      }

      async render() {
        let items = [];
        try {
          items = await window.BluorngWishlist.items();
        } catch (error) {
          console.error('Wishlist could not be loaded', error);
        }
        const products = (await Promise.all(items.map((item) => this.product(item)))).filter(Boolean);

        this.grid.replaceChildren(...products.map((entry) => this.card(entry)));
        this.status.hidden = true;
        this.empty.hidden = products.length > 0;
        if (this.banner) this.banner.hidden = products.length === 0;
        this.updateBar(products);
      }

      async product(item) {
        try {
          const path = new URL(item.du, window.location.origin).pathname.replace(/\/$/, '');
          const response = await fetch(`${path}.js`);
          if (!response.ok) return null;
          const product = await response.json();
          const variant = product.variants.find((v) => String(v.id) === String(item.epi)) || product.variants[0];
          return { item, product, variant };
        } catch {
          return null;
        }
      }

      card({ product, variant }) {
        const card = this.template.content.firstElementChild.cloneNode(true);
        const url = `${product.url}?variant=${variant.id}`;
        const image = (variant.featured_image && variant.featured_image.src) || product.featured_image;

        card.querySelectorAll('[data-card-link]').forEach((link) => (link.href = url));
        card.querySelector('[data-card-title]').textContent = product.title;
        card.querySelectorAll('[data-card-size]').forEach((size) => (size.textContent = variant.title));
        if (product.variants.length === 1) card.dataset.singleVariant = '';

        const img = card.querySelector('[data-card-image]');
        if (image) {
          const src = image.startsWith('//') ? `https:${image}` : image;
          const joiner = src.includes('?') ? '&' : '?';
          img.src = `${src}${joiner}width=600`;
          img.srcset = [400, 600, 900].map((w) => `${src}${joiner}width=${w} ${w}w`).join(', ');
          img.sizes = '(max-width: 950px) 33vw, 25vw';
          img.alt = product.title;
        }

        card.querySelector('[data-card-price]').textContent = this.money(variant.price);
        if (variant.compare_at_price > variant.price) {
          const compare = card.querySelector('[data-card-compare]');
          compare.textContent = this.money(variant.compare_at_price);
          compare.hidden = false;
        }
        if (!variant.available) {
          card.classList.add('main-wishlist__card--sold-out');
          card.querySelector('[data-card-sold-out]').hidden = false;
        }

        const remove = card.querySelector('[data-card-remove]');
        remove.addEventListener('click', async () => {
          remove.disabled = true;
          card.classList.add('main-wishlist__card--removing');
          try {
            await window.BluorngWishlist.remove(product.id, variant.id);
          } catch (error) {
            console.error('Wishlist remove failed', error);
            remove.disabled = false;
            card.classList.remove('main-wishlist__card--removing');
          }
        });
        return card;
      }

      updateBar(products) {
        const inStock = products.filter(({ variant }) => variant.available);
        const total = inStock.reduce((sum, { variant }) => sum + variant.price, 0) / 100;
        const totalText = `Rs. ${total.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        this.inStockVariants = inStock.map(({ variant }) => variant.id);

        this.bar.querySelector('[data-bar-count]').textContent = products.length;
        this.bar.querySelector('[data-bar-in-stock]').textContent = inStock.length;
        this.bar.querySelector('[data-bar-selected]').textContent = inStock.length;
        this.bar.querySelectorAll('[data-bar-total]').forEach((el) => (el.textContent = totalText));
        this.checkoutButton.classList.toggle('stock-out', inStock.length === 0);
        this.bar.classList.add('show-info');
      }

      // Same flow as before: every in-stock item goes to the cart, then straight to checkout.
      async checkout() {
        if (this.checkingOut || !this.inStockVariants.length) return;
        this.checkingOut = true;
        this.checkoutButton.classList.add('disabled');
        try {
          const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: this.inStockVariants.map((id) => ({ id, quantity: 1 })) }),
          });
          if (response.ok) {
            window.location.href = '/checkout';
            return;
          }
        } catch (error) {
          console.error('Wishlist checkout failed', error);
        }
        this.checkingOut = false;
        this.checkoutButton.classList.remove('disabled');
      }

      money(cents) {
        const format = this.dataset.moneyFormat || '{{amount}}';
        const value = (cents / 100).toFixed(2);
        const [whole, decimals] = value.split('.');
        const withCommas = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return format
          .replace('{{amount_no_decimals}}', withCommas)
          .replace('{{amount_with_comma_separator}}', `${whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.')},${decimals}`)
          .replace('{{amount}}', `${withCommas}.${decimals}`);
      }
    }
  );
}
